import { env } from './env'

/**
 * QA correlation telemetry.
 *
 * When `VITE_QA_TELEMETRY` is on, every outbound request ATTEMPT carries a fresh
 * `x-request-id` (UUIDv4). The API already honors and sanitizes an inbound
 * `x-request-id` (`apps/api` request-context middleware), so this becomes the
 * join key that lets a QA timeline stitch a browser network event to the exact
 * API log line it produced — the browser↔backend correlation from
 * `07-BACKEND-CORRELATION.md` (C2). No response handling is involved.
 *
 * "Per attempt" is deliberate: call these at the header-build spot of each fetch,
 * so a 401-refresh retry (which re-invokes the wrapper) or an SSE transport
 * fallback (which issues a second fetch) mints its OWN id rather than reusing the
 * first attempt's. HTTP/2 multiplexing is why an explicit id — not request time
 * or URL order — is the reliable join.
 *
 * Default OFF ⇒ returns `null` / `{}` ⇒ byte-identical outbound headers.
 */
export function qaRequestId(): string | null {
  return env.qaTelemetry ? crypto.randomUUID() : null
}

/**
 * Spread form of {@link qaRequestId} for call sites that build headers as an
 * object literal (e.g. the SSE wrappers): `{ ...headers, ...qaRequestIdHeaders() }`.
 * Returns `{}` when telemetry is off, so the spread is a no-op.
 */
export function qaRequestIdHeaders(): Record<string, string> {
  const id = qaRequestId()
  return id ? { 'x-request-id': id } : {}
}
